import { Observer } from "@babylonjs/core/Misc/observable";
import { Scene } from "@babylonjs/core/scene";

import { TraceBehavior } from "./tracebehavior";
import { TimelineData, TimelineKeyframe } from "../scenedata/behaviors/timelinedata";
import { ScriptBehavior } from "../scripting/ScriptBehavior";
import type { SceneObject } from "../core/SceneObject";
import {
    hasPlayableClock, hasPlayableSampled, hasPlayableState, isPlayable,
    type Playable, type PlayableClock,
} from "./playable";
import { Logger } from "../utils/Logger";

/** Master-clock snap threshold (s): farther apart than this, the timeline jumps to the medium. */
export const TIMELINE_CLOCK_SNAP_SECONDS = 0.25;
/** Master-clock slew time constant (s): within the snap threshold, error decays over ~this long. */
export const TIMELINE_CLOCK_SLEW_SECONDS = 0.1;

/**
 * Play a Playable the Timeline just seeked — unless it is already running.
 * `seek()` leaves play state alone, so a running medium is already playing
 * from the new position; calling `play()` on it again is at best redundant
 * and, for a Playable whose `play()` (re)starts a session, a rewind.
 */
function resumePlayable(p: Playable): void {
    // An owned sampled Playable: the Timeline's own clock runs it.
    if (hasPlayableSampled(p) && p.isSampling()) return;
    if (hasPlayableState(p) && p.isPlaying()) return;
    void p.play();
}

/** Put a Playable at Timeline time `t`: claim + sample it when it can be sampled, else seek it. */
function placePlayable(p: Playable, t: number): void {
    if (hasPlayableSampled(p)) p.sampleAt(t);
    else p.seek(t);
}

/** Sentinel in the applied-cue map: no cue ≤ t, so the target's cue layer is released. */
const NO_CUE = -1;

/**
 * Timeline behavior — a unified scene clock.
 *
 * Discovers Playable behaviors on this object and its descendants (any depth,
 * nested Timelines excluded), driving them via play/pause/seek. Supports
 * looping, speed control, keyframe events and `active` state cues, and an
 * optional master clock (`clock`) that the timeline follows while playing
 * (RECORDINGS_SPEC §4.2).
 *
 * Master clock, per frame while playing at speed 1, with `c` the medium's
 * `getClockTime()` and `t` the timeline time:
 * - `c` null → `t += dt` (scene time, as without a clock);
 * - `|c − t| > 0.25` → snap `t = c`, and re-seek every NON-master Playable to
 *   the new `t` (the master is never re-seeked during play);
 * - otherwise predict-then-correct: `t' = t + dt; t = t' + (c − t')·min(1, dt/0.1)`.
 *   The prediction term removes the steady 0.1 s lag a pure first-order
 *   slew toward a moving clock would carry.
 * With `speed` ≠ 1 the clock is ignored: media play at their own rate (no
 * media Playable sees `speed`), so following one would silently cancel the speed.
 *
 * Frame order: the per-frame step runs on `onBeforeAnimationsObservable`, so
 * everything it does in a frame — advance, snap, loop, end — lands before
 * Babylon evaluates animations — a clip bound into an avatar
 * (`TRACE_animation_binding`) included — so the frame shows this frame's time,
 * and anything that follows animated state after evaluation
 * (`onAfterAnimationsObservable`) sees it too.
 *
 * Sampled Playables (`PlayableSampled`, a keyframe `Animation`) are CLAIMED
 * on play from stopped, seek and loop, and while the claim holds they never
 * run on their own clock: they are sampled at `t` every frame and hold the
 * last frame at the end. Anything else playing the clip ends the claim, and
 * the Timeline leaves it alone until it claims it again. Media Playables are
 * seeked and played as before and run open-loop, except the master.
 */
export class Timeline extends TraceBehavior<TimelineData> implements Playable {
    static readonly behaviorType = "Timeline" as const;

    private _currentTime = 0;
    private _playing = false;
    private _renderObserver: Observer<Scene> | null = null;
    /** Play-mode watch that places sampled Playables joining the subtree (see _placeNewSampled). */
    private _restObserver: Observer<Scene> | null = null;
    /** Sampled Playables already placed (or found driven by something else) this play-mode session. */
    private _placedSampled = new WeakSet<object>();

    /** Set of keyframe indices that have already fired in the current playthrough */
    private _firedKeyframes: Set<number> = new Set();

    /**
     * Resolved master clock, null included, keyed by the path and the scene's
     * structure revision; dropped on play and on `clock` commits.
     */
    private _clockCache: { path: string; rev: number; clock: (PlayableClock & object) | null } | null = null;
    /** Playables in the subtree (+ master), keyed by the scene's structure revision. */
    private _playablesCache: { rev: number; list: readonly Playable[] } | null = null;
    /** Clock paths already warned about, so a bad path logs once rather than every frame. */
    private _clockWarned = new Set<string>();

    /** Per cue target: index of the cue whose state was last applied, or NO_CUE. */
    private _cueApplied = new Map<SceneObject, number>();
    /** Resolved cue targets by path ('' = self). Cleared on keyframe/topology changes. */
    private _cueTargets = new Map<string, SceneObject>();
    private _cueTargetsRevision = NaN;

    // ── Public API ──

    /** Current playback time in seconds */
    getTime(): number {
        return this._currentTime;
    }

    /** Total duration in seconds */
    getDuration(): number {
        return this._resolved.duration ?? 10;
    }

    /** Whether the timeline is currently playing */
    isPlaying(): boolean {
        return this._playing;
    }

    /** Start or resume playback */
    play(): void {
        if (this._playing) return;

        // Reset to start if the timeline had reached the end
        if (this._currentTime >= this.getDuration()) {
            this._currentTime = 0;
            this._firedKeyframes.clear();
        }

        this._playing = true;
        // Re-resolve: the hierarchy may have changed in ways no revision saw
        // (a stub scene without one).
        this._clockCache = null;
        this._playablesCache = null;
        this._startRenderLoop();
        this._applyStateCues(this._currentTime, true);
        for (const p of this._getPlayables()) {
            placePlayable(p, this._currentTime);
            resumePlayable(p);
        }
    }

    /** Pause playback */
    pause(): void {
        if (!this._playing) return;
        this._playing = false;
        this._stopRenderLoop();
        for (const p of this._getPlayables()) {
            p.pause();
        }
    }

    /**
     * Stop: pause and return to t = 0 — the rest state (state cues and
     * sampled Playables at t = 0, media at their start). With play/pause/seek
     * this makes a Timeline a Playable, so the media graph ops
     * (`trace/playMedia`, `pauseMedia`, `stopMedia`, `isMediaPlaying`) drive
     * it like any other medium — a `Recording` node's activation graph
     * (RECORDINGS_SPEC §4.5) targets its Timeline this way.
     */
    stop(): void {
        this.pause();
        this.seek(0);
    }

    /**
     * Seek to a time (clamped to [0, duration]).
     * @param time - Target time in seconds, or delta when mode is "relative"
     * @param mode - "absolute" (default) or "relative" — add delta to current time
     */
    seek(time: number, mode: "absolute" | "relative" = "absolute"): void {
        const duration = this.getDuration();
        const target = mode === "relative" ? this._currentTime + time : time;
        const newTime = Math.max(0, Math.min(target, duration));

        // Only re-enable keyframes that are after the new position.
        // Seeking backward clears all (they should re-fire on replay).
        // Seeking forward keeps already-fired keyframes suppressed.
        if (newTime < this._currentTime) {
            this._firedKeyframes.clear();
        }

        this._currentTime = newTime;
        this._applyStateCues(newTime, true);
        for (const p of this._getPlayables()) {
            placePlayable(p, this._currentTime);
            // If the timeline is playing, ensure playables resume after seek
            // (a playable that seek() leaves paused must run again)
            if (this._playing) {
                resumePlayable(p);
            }
        }
    }

    // ── Lifecycle ──

    protected onPlayModeChanged(playing: boolean): void {
        if (playing) {
            this._currentTime = 0;
            this._firedKeyframes.clear();
            // State as of t = 0 holds from the start of play mode, whether or not
            // the timeline auto-plays…
            this._applyStateCues(0, true);
            // …and so does every sampled Playable's t = 0 (RECORDINGS_SPEC
            // §4.5: a recording rests in its preview pose, its first frame).
            // Playables that attach later — a subtree still loading — are
            // placed on the first frame they appear.
            this._startRestWatch();
            this._placeNewSampled();
            if (this._resolved.playOnStart !== false) {
                this.play();
            }
        } else {
            this._stopRestWatch();
            this._playing = false;
            this._stopRenderLoop();
            this._currentTime = 0;
            this._firedKeyframes.clear();
            for (const p of this._getPlayables()) {
                p.stop();
            }
            // Leaving play mode releases the cue layer, whatever the cues said
            // last. The state layer is released by Stop itself, before this.
            this._releaseCues();
        }
    }

    protected onDetach(): void {
        this._stopRestWatch();
        this._stopRenderLoop();
        this._releaseCues();
    }

    protected async applyCommit(patch: Partial<TimelineData>, prev: TimelineData): Promise<void> {
        // Presence, not truthiness (DESIGN_PRINCIPLES §10.7): clearing a field
        // — `{ keyframes: undefined }`, or an undo snapshot without it — is a
        // change too. Compared against the previous authored data so both
        // commit() and commitSnapshot() are seen.
        const authored = this.getAuthored();
        const changed = (key: keyof TimelineData): boolean =>
            key in patch || JSON.stringify(prev[key]) !== JSON.stringify(authored[key]);
        if (changed('clock')) {
            this._clockCache = null;
            this._playablesCache = null;
        }
        if (changed('keyframes')) {
            const wasApplied = this._cueApplied.size > 0;
            this._releaseCues();
            this._cueTargets.clear();
            if (wasApplied) this._applyStateCues(this._currentTime, true);
        }
        // Clamp current time if duration shrank, and sync playables to the new position
        if (patch.duration !== undefined && this._currentTime > patch.duration) {
            this._currentTime = patch.duration;
            for (const p of this._getPlayables()) {
                placePlayable(p, this._currentTime);
            }
        }
    }

    // ── Internals ──

    /**
     * The scene's structure revision (`TraceScene.structureRevision`): bumped
     * by every create/delete/reparent/rename and every behavior add/remove.
     * NaN when the scene has none (test stubs) — then nothing is cached.
     */
    private _structureRevision(): number {
        const rev = (this.sceneObject.traceScene as { structureRevision?: number }).structureRevision;
        return typeof rev === "number" ? rev : NaN;
    }

    /**
     * Playable behaviors on this object and its descendants (excluding self
     * and nested Timelines), plus the master clock if it lives outside the
     * subtree — the master is seeked with everything else on play/seek/loop.
     * Cached per structure revision: the per-frame step reads it every frame,
     * and a walk of the subtree per frame is what the cache avoids.
     */
    private _getPlayables(): readonly Playable[] {
        const rev = this._structureRevision();
        const cached = this._playablesCache;
        if (cached && cached.rev === rev) return cached.list;
        const playables: Playable[] = [];
        this._collectPlayables(this.sceneObject, playables);
        const master = this._getClock();
        if (master && isPlayable(master) && !playables.includes(master)) {
            playables.push(master);
        }
        this._playablesCache = Number.isNaN(rev) ? null : { rev, list: playables };
        return playables;
    }

    private _collectPlayables(obj: SceneObject, out: Playable[]): void {
        for (const behavior of obj.getBehaviors()) {
            // Skip self and nested Timelines (they manage their own playback)
            if (behavior !== this && !(behavior instanceof Timeline) && isPlayable(behavior)) {
                out.push(behavior);
            }
        }
        for (const child of obj.getChildren()) {
            // A nested Timeline owns its whole subtree: collecting through it
            // would drive those Playables from two clocks.
            if (child.getBehaviors().some(b => b instanceof Timeline)) continue;
            this._collectPlayables(child, out);
        }
    }

    /**
     * The master clock named by `clock`: the first clock-capable Playable on
     * the resolved node. Null when unset or unresolvable (warned once per
     * path). The answer — a miss included — is cached per path and structure
     * revision, so a node that is created, renamed or given its AudioClip
     * later is picked up, and a bad path is not re-resolved every frame.
     */
    private _getClock(): (PlayableClock & object) | null {
        const path = this._resolved.clock;
        if (!path) return null;
        const rev = this._structureRevision();
        const cached = this._clockCache;
        if (cached && cached.path === path && cached.rev === rev) return cached.clock;
        const clock = this._resolveClock(path);
        this._clockCache = Number.isNaN(rev) && !clock ? null : { path, rev, clock };
        return clock;
    }

    private _resolveClock(path: string): (PlayableClock & object) | null {
        const obj = this.sceneObject.traceScene.resolveSceneObject(path, this.sceneObject);
        if (obj) {
            for (const behavior of obj.getBehaviors()) {
                if (behavior !== this && !(behavior instanceof Timeline) && hasPlayableClock(behavior)) {
                    return behavior;
                }
            }
        }
        if (!this._clockWarned.has(path)) {
            this._clockWarned.add(path);
            Logger.warn(obj
                ? `[Timeline] clock "${path}" has no clock-capable Playable (AudioClip/Video) — advancing on scene time`
                : `[Timeline] clock "${path}" not found — advancing on scene time`);
        }
        return null;
    }

    private _startRenderLoop(): void {
        if (this._renderObserver) return;

        this._renderObserver = this.scene.onBeforeAnimationsObservable.add(() => {
            if (!this._playing) return;

            const dt = this.scene.getEngine().getDeltaTime() / 1000;
            const speed = this._resolved.speed ?? 1;
            const duration = this.getDuration();

            // Guard against zero/negative duration (% 0 produces NaN)
            if (duration <= 0) return;

            const prevTime = this._currentTime;
            const master = speed === 1 ? this._getClock() : null;
            const c = master ? master.getClockTime() : null;
            let snapped = false;
            if (c !== null && Number.isFinite(c)) {
                if (Math.abs(c - prevTime) > TIMELINE_CLOCK_SNAP_SECONDS) {
                    this._currentTime = c;
                    snapped = true;
                } else {
                    const predicted = prevTime + dt;
                    this._currentTime = predicted + (c - predicted) * Math.min(1, dt / TIMELINE_CLOCK_SLEW_SECONDS);
                }
            } else {
                this._currentTime += dt * speed;
            }

            if (snapped && this._currentTime < prevTime) {
                // A backward snap is a seek: keyframes after the new time re-arm.
                this._firedKeyframes.clear();
            } else {
                // Check keyframes in the interval [prevTime, currentTime]
                this._checkKeyframes(prevTime, this._currentTime);
            }

            if (snapped) {
                // The master jumped (stall recovery, late start, …): bring the
                // open-loop Playables to it. Never the master itself.
                const t = Math.max(0, Math.min(this._currentTime, duration));
                for (const p of this._getPlayables()) {
                    if ((p as object) === master || hasPlayableSampled(p)) continue;
                    p.seek(t);
                    resumePlayable(p);
                }
            }

            // Handle end-of-timeline
            if (this._currentTime >= duration) {
                if (this._resolved.loop) {
                    this._currentTime = this._currentTime % duration;
                    this._firedKeyframes.clear();
                    // Events in the wrapped-off residual [0, t] fire this frame.
                    this._checkKeyframes(0, this._currentTime);
                    this._applyStateCues(this._currentTime, true);
                    // Re-sync playables so non-looping children restart from the new time
                    for (const p of this._getPlayables()) {
                        placePlayable(p, this._currentTime);
                        resumePlayable(p);
                    }
                } else {
                    this._currentTime = duration;
                    this._playing = false;
                    this._stopRenderLoop();
                    this._applyStateCues(this._currentTime);
                    for (const p of this._getPlayables()) {
                        // A sampled Playable holds the last frame, agreeing
                        // with getTime(); media stop (rewind) as before.
                        if (hasPlayableSampled(p) && p.isSampling()) p.sampleAt(duration);
                        else p.stop();
                    }
                }
            } else {
                this._applyStateCues(this._currentTime);
                // Sampled Playables show exactly this frame's time. This runs
                // before Babylon evaluates animations, so this frame's
                // evaluation — and anything that follows it — shows this time.
                // Only the ones this Timeline owns: a clip something else
                // started since (a graph's trace/playAnimation) runs free.
                for (const p of this._getPlayables()) {
                    if (hasPlayableSampled(p) && p.isSampling()) p.sampleAt(this._currentTime);
                }
            }
        });
    }

    /**
     * Place every sampled Playable of the subtree this play-mode session has
     * not seen yet at the Timeline's current time (claiming it), so a
     * Timeline at rest shows its time — t = 0 on entering play mode — and a
     * Playable that joins later (a subtree still loading) joins at that time.
     * Each Playable is considered ONCE per session: one that something else
     * already drives (playing, or sampled by another Timeline) is left alone,
     * and a claim that something else later ends is not taken back.
     */
    private _placeNewSampled(): void {
        for (const p of this._getPlayables()) {
            if (!hasPlayableSampled(p) || this._placedSampled.has(p)) continue;
            if (p.isSampling() || (hasPlayableState(p) && p.isPlaying())) {
                this._placedSampled.add(p);
                continue;
            }
            p.sampleAt(this._currentTime);
            // Not claimable yet (its clip not bound yet): try again next frame.
            if (p.isSampling()) this._placedSampled.add(p);
        }
    }

    private _startRestWatch(): void {
        if (this._restObserver || !this.scene) return;
        this._restObserver = this.scene.onBeforeAnimationsObservable.add(() => this._placeNewSampled());
    }

    private _stopRestWatch(): void {
        if (this._restObserver && this.scene) this.scene.onBeforeAnimationsObservable.remove(this._restObserver);
        this._restObserver = null;
        this._placedSampled = new WeakSet();
    }

    private _stopRenderLoop(): void {
        if (this._renderObserver && this.scene) {
            this.scene.onBeforeAnimationsObservable.remove(this._renderObserver);
            this._renderObserver = null;
        }
    }

    private _checkKeyframes(prevTime: number, currentTime: number): void {
        const keyframes = this._resolved.keyframes;
        if (!keyframes?.length) return;

        for (let i = 0; i < keyframes.length; i++) {
            if (this._firedKeyframes.has(i)) continue;

            const kf = keyframes[i];
            if (kf.time >= prevTime && kf.time <= currentTime) {
                this._firedKeyframes.add(i);
                // A pure state cue carries no event and fires none.
                if (kf.event) this._fireKeyframeEvent(kf.event, kf.target);
            }
        }
    }

    // ── State cues ──

    private _resolveCueTarget(kf: TimelineKeyframe): SceneObject | null {
        const rev = this._structureRevision();
        if (rev !== this._cueTargetsRevision) {
            this._cueTargets.clear();
            this._cueTargetsRevision = rev;
            for (const target of this._cueApplied.keys()) {
                if (target.transform.isDisposed()) this._cueApplied.delete(target);
            }
        }
        const key = kf.target ?? "";
        const cached = this._cueTargets.get(key);
        if (cached) return cached;
        const obj = kf.target
            ? this.sceneObject.traceScene.resolveSceneObject(kf.target, this.sceneObject)
            : this.sceneObject;
        if (obj) this._cueTargets.set(key, obj);
        return obj ?? null;
    }

    /**
     * Put every cue target in its state as of `t`: its latest cue with
     * `time ≤ t` (the later keyframe wins a tie), else none: the cue layer is
     * released and the state and the authored flag show through. Without
     * `force`, a target is only written when its governing cue changed since
     * the last application — so the per-frame pass is idle between cues and
     * does not fight other writers (scripts, states) in between. Seeks, play
     * and loop force, which makes scrubbing deterministic.
     */
    private _applyStateCues(t: number, force = false): void {
        const keyframes = this._resolved.keyframes;
        if (!keyframes?.length) return;

        const governing = new Map<SceneObject, number>();
        for (let i = 0; i < keyframes.length; i++) {
            const kf = keyframes[i];
            if (typeof kf.active !== "boolean") continue;
            const target = this._resolveCueTarget(kf);
            if (!target) continue;
            const current = governing.get(target) ?? NO_CUE;
            if (kf.time <= t && (current === NO_CUE || keyframes[current].time <= kf.time)) {
                governing.set(target, i);
            } else if (!governing.has(target)) {
                governing.set(target, NO_CUE);
            }
        }

        for (const [target, index] of governing) {
            if (!force && this._cueApplied.get(target) === index) continue;
            this._cueApplied.set(target, index);
            // A runtime layer, never setActive: that writes the authored flag, which
            // every save copies, and a cue's state is not the object's own.
            target.setActiveCue(index === NO_CUE ? undefined : keyframes[index].active === true);
        }
    }

    /** Hand every touched cue target back to its state and its authored flag. */
    private _releaseCues(): void {
        for (const target of this._cueApplied.keys()) target.setActiveCue(undefined);
        this._cueApplied.clear();
    }

    private _fireKeyframeEvent(event: string, target?: string): void {
        const traceScene = this.sceneObject.traceScene;

        // Resolve target — defaults to this object
        const targetObject = target
            ? traceScene.resolveSceneObject(target, this.sceneObject)
            : this.sceneObject;

        if (!targetObject) {
            Logger.warn(`[Timeline] Keyframe target not found: ${target}`);
            return;
        }

        // Dispatch to ScriptBehaviors on the target object.
        // BehaviorGraph handles its own events natively via FlowGraph event blocks.
        // TODO: When event/send + event/receive blocks are implemented, fire
        // custom events into BehaviorGraph here as well.
        for (const behavior of targetObject.getBehaviors()) {
            if (behavior instanceof ScriptBehavior) {
                behavior.fireEvent(event).catch(err => {
                    Logger.error(`[Timeline] Keyframe event "${event}" failed:`, err);
                });
            }
        }
    }
}
